import { Check, LoaderCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { ApiRequestError, verifyEmail } from "./api";
import { useAuth } from "./AuthProvider";
import { AuthShell } from "./AuthShell";

type VerificationState =
  | { status: "working" }
  | { status: "complete" }
  | { status: "error"; message: string };

export const VerifyEmailPage = () => {
  const location = useLocation();
  const { setAuthenticated } = useAuth();
  const started = useRef(false);
  const token = new URLSearchParams(location.search).get("token") ?? "";
  const [state, setState] = useState<VerificationState>({
    status: "working",
  });

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!token) {
      setState({
        status: "error",
        message: "This verification link is incomplete.",
      });
      return;
    }

    void verifyEmail({ token })
      .then((viewer) => {
        setAuthenticated(viewer);
        setState({ status: "complete" });
      })
      .catch((error: unknown) => {
        setState({
          status: "error",
          message:
            error instanceof ApiRequestError
              ? error.message
              : "We could not verify this email.",
        });
      });
  }, [setAuthenticated, token]);

  return (
    <AuthShell
      title={
        state.status === "working"
          ? "Verifying your email"
          : state.status === "complete"
            ? "Email verified"
            : "Verification failed"
      }
    >
      <section className="auth-success" aria-live="polite">
        <span className="auth-success__icon" aria-hidden="true">
          {state.status === "working" ? (
            <LoaderCircle className="auth-spinner" size={21} />
          ) : (
            <Check size={21} />
          )}
        </span>
        {state.status === "working" ? (
          <p>Finishing your Reelio account…</p>
        ) : state.status === "complete" ? (
          <>
            <h2>Your workspace is ready.</h2>
            <p>You are signed in and can continue to Reelio.</p>
            <Link className="auth-submit" to="/app">
              Open workspace
            </Link>
          </>
        ) : (
          <>
            <h2>The link could not be used.</h2>
            <p>{state.message}</p>
            <Link className="auth-secondary" to="/auth/login">
              Return to login
            </Link>
          </>
        )}
      </section>
    </AuthShell>
  );
};
